import React, { useState } from 'react';

import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import KeyStatTile from 'CommonComponents/KeyStatTile';

export default function GeneralRiskWeightedValueTable(props) {
    return (
        <div className="table__card">
            <h4 className="table__header tw-mb-6">{$t('Risk Weighted Value')}</h4>
            <div className="tw-grid tw-grid-cols-5 tw-gap-x-5 tw-gap-y-3 tw-mb-6">
                <KeyStatTile title={$t('Total Risks')} value={formatToFixedMoneyWithLocale(props.summary.risks_amount)} />
                <KeyStatTile title={$t('Total Risk Weighted Value')} value={formatToFixedMoneyWithLocale(props.summary.risks_weighted_value)} />
                <KeyStatTile title={$t('Total Opportunities')} value={formatToFixedMoneyWithLocale(props.summary.opportunities_amount)} />
                <KeyStatTile
                    title={$t('Total Opportunities Weighted Value')}
                    value={formatToFixedMoneyWithLocale(props.summary.opportunities_weighted_value)}
                />
                <KeyStatTile title={$t('Net Value')} value={formatToFixedMoneyWithLocale(props.summary.net)} />
            </div>
            <div className="tw-space-y-6">
                <RisksTable risks={props.risks} summary={props.summary} isLoading={props.isLoading} onUpdate={props.updateRiskWeightedValue} />
                <OpportunitiesTable
                    opportunities={props.opportunities}
                    summary={props.summary}
                    isLoading={props.isLoading}
                    onUpdate={props.updateRiskWeightedValue}
                />
            </div>
        </div>
    );
}

function RisksTable({ risks, summary, isLoading, onUpdate }) {
    const [editing, setEditing] = useState(null);

    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table">
                <thead>
                    <tr>
                        <th className="tw-w-52">{$t('Risk Description')}</th>
                        <th className="tw-min-w-72">{$t('Detailed Description')}</th>
                        <th className="tw-w-40">{$t('AFC')}</th>
                        <th className="tw-w-40">{$t('Value')}</th>
                        <th className="tw-w-40">{$t('Weighting')}</th>
                        <th className="tw-w-40">{$t('Weighted Value')}</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading && risks.length === 0 && (
                        <tr>
                            <td colSpan={6}>{$t('Loading')}...</td>
                        </tr>
                    )}
                    {!isLoading && risks.length === 0 && (
                        <tr>
                            <td colSpan={6}>{$t('No risks with a value')}</td>
                        </tr>
                    )}
                    {risks.map((risk) => (
                        <tr key={risk.id}>
                            <td>{risk.name}</td>
                            <td>{risk.description}</td>
                            <td>{risk.afc_enabled ? $t('Yes') : $t('No')}</td>
                            <td className="inbud-numeric-cell">{formatFigure(risk.amount)}</td>
                            <td className="inbud-numeric-cell" onClick={() => setEditing(risk.id)}>
                                {editing === risk.id ? (
                                    <input
                                        type="number"
                                        className="input text-input !tw-bg-white tw-text-right"
                                        autoFocus
                                        defaultValue={risk.percentage}
                                        min={0}
                                        max={100}
                                        step="1"
                                        onBlur={(e) => {
                                            if (e.target.value != risk.percentage) {
                                                onUpdate('risk', risk.id, e.target.value);
                                            }
                                            setEditing(null);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                onUpdate('risk', risk.id, e.target.value);
                                                setEditing(null);
                                            } else if (e.key === 'Escape') {
                                                setEditing(null);
                                            }
                                        }}
                                    />
                                ) : (
                                    <span className="tw-text-blue-75">{risk.percentage}%</span>
                                )}
                            </td>
                            <td className="inbud-numeric-cell">{formatFigure(risk.weighted_value)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td>{$t('Totals')}</td>
                        <td></td>
                        <td></td>
                        <td className="inbud-numeric-cell">{formatFigure(summary.risks_amount)}</td>
                        <td className="inbud-numeric-cell"></td>
                        <td className="inbud-numeric-cell">{formatFigure(summary.risks_weighted_value)}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

function OpportunitiesTable({ opportunities, summary, isLoading, onUpdate }) {
    const [editing, setEditing] = useState(null);

    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table">
                <thead>
                    <tr>
                        <th className="tw-w-52">{$t('Opportunity Description')}</th>
                        <th className="tw-min-w-72">{$t('Detailed Description')}</th>
                        <th className="tw-w-40">{$t('AFC')}</th>
                        <th className="tw-w-40">{$t('Value')}</th>
                        <th className="tw-w-40">{$t('Weighting')}</th>
                        <th className="tw-w-40">{$t('Weighted Value')}</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading && opportunities.length === 0 && (
                        <tr>
                            <td colSpan={6}>{$t('Loading')}...</td>
                        </tr>
                    )}
                    {!isLoading && opportunities.length === 0 && (
                        <tr>
                            <td colSpan={6}>{$t('No opportunities with a value')}</td>
                        </tr>
                    )}
                    {opportunities.map((opportunity) => (
                        <tr key={opportunity.id}>
                            <td>{opportunity.name}</td>
                            <td>{opportunity.description}</td>
                            <td>{opportunity.afc_enabled ? $t('Yes') : $t('No')}</td>
                            <td className="inbud-numeric-cell">{formatFigure(opportunity.amount)}</td>
                            <td className="inbud-numeric-cell" onClick={() => setEditing(opportunity.id)}>
                                {editing === opportunity.id ? (
                                    <input
                                        type="number"
                                        className="input text-input !tw-bg-white tw-text-right"
                                        autoFocus
                                        defaultValue={opportunity.percentage}
                                        min={0}
                                        max={100}
                                        step="1"
                                        onBlur={(e) => {
                                            if (e.target.value != opportunity.percentage) {
                                                onUpdate('opportunity', opportunity.id, e.target.value);
                                            }
                                            setEditing(null);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                onUpdate('opportunity', opportunity.id, e.target.value);
                                                setEditing(null);
                                            } else if (e.key === 'Escape') {
                                                setEditing(null);
                                            }
                                        }}
                                    />
                                ) : (
                                    <span className="tw-text-blue-75">{opportunity.percentage}%</span>
                                )}
                            </td>
                            <td className="inbud-numeric-cell">{formatFigure(opportunity.weighted_value)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td>{$t('Totals')}</td>
                        <td></td>
                        <td></td>
                        <td className="inbud-numeric-cell">{formatFigure(summary.opportunities_amount)}</td>
                        <td className="inbud-numeric-cell"></td>
                        <td className="inbud-numeric-cell">{formatFigure(summary.opportunities_weighted_value)}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}
